import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { useApolloClient } from '@apollo/client/react';

import { useAbeetContext } from '@clutter/abeet';
import { useQueryParams } from '@clutter/hooks';

import { PricingSetFragment } from '@graphql/platform';

import { AgentBanner } from '@root/components/checkout/agent_banner';
import { PreviousBookingWarningModal } from '@root/components/checkout/context/previous_booking_warning_modal';
import { useAgentBookingLeadToken } from '@root/components/checkout/helpers/agent_booking';
import { LoadingState } from '@root/components/checkout/product_pages/loading_state';
import { getInitialValues } from '@root/components/checkout/utilities/initial_values';
import { CHALLENGER_DEFAULT_PLAN_SIZE } from '@root/components/checkout/utilities/persistence';
import { WTProvider, WT_VISITOR_TOKEN } from '@root/initializers/wt';

import { Action, useClientDataContext } from '@shared/client_data_context';
import {
  FlowConfig,
  UseFlowResult,
  useFlow,
} from '@shared/flow/flow_container';
import { useServerDataContext } from '@shared/server_data_context';

import { PricingSummary } from '@utils/hooks/pricing/use_pricing_summary';
import { useStoragePricingValues } from '@utils/hooks/pricing/use_storage_pricing_values';
import {
  hasStorageBookingFunnelEnrollment,
  isChallengerStorageBookingFunnelForInit,
  resolveStorageCheckoutFlowVersion,
  useStorageBookingFunnelAssignment,
} from '@utils/hooks/storage_booking_funnel_test';
import { useEligibleForDisposalZIP } from '@utils/hooks/use_eligible_for_disposal_zip';
import { plausibleZIP } from '@utils/plausible_zip';

import {
  NavigationState,
  useNavigationState,
} from './context/navigation_state';
import { SharedCheckoutData, StorageCheckoutData } from './data';
import { transformStorageStepTransitionData } from './helpers/transform_step_transition_data';
import { SmartStorage } from './product_pages/steps/arrays/smart_storage';
import {
  CheckoutType,
  SharedCheckoutStepProps,
  StorageCheckoutStep,
  StorageCheckoutStepProps,
  StorageStepDescriptor,
} from './types';
import { parseCheckoutQueryString } from './utilities/checkout_url';
import { flushPendingStorageDraftUpsert } from './utilities/drafts';
import { InitialValues } from './utilities/initial_values/types';
import { persistStorageSessionCheckoutData } from './utilities/persistence';

type StorageFlowState = UseFlowResult<
  StorageCheckoutData,
  StorageCheckoutStepProps
>;

type StorageContextValue = {
  flowVersion: string;
  flowState: StorageFlowState;
  resolvedSteps: StorageStepDescriptor[];
  navigationState: NavigationState<StorageCheckoutStep>;
  pricingSet?: PricingSetFragment;
  pricingSummary?: PricingSummary;
  /** The pricing summary for a 5x5 */
  defaultPricingSummary?: PricingSummary;
  disposalEligible: boolean | undefined;
};

const StorageCheckoutContext = createContext<StorageContextValue | undefined>(
  undefined,
);

export const useStorageCheckoutContext = () => {
  const value = useContext(StorageCheckoutContext);
  if (!value) throw new Error('CheckoutContextProvider is missing!');
  return value;
};

/** Classic smart-storage checkout context, or `undefined` outside that provider (e.g. multi-page V2). */
export const useMaybeStorageCheckoutContext = () =>
  useContext(StorageCheckoutContext);

export const useSharedCheckoutContext = () => {
  const storageValue = useContext(StorageCheckoutContext);
  if (!storageValue) throw new Error('CheckoutContextProvider is missing!');
  return storageValue;
};

/** Classic checkout context only — returns undefined under multi-page `CheckoutContextProvider`. */
export const useMaybeSharedCheckoutContext = ():
  | StorageContextValue
  | undefined => {
  const storageValue = useContext(StorageCheckoutContext);
  return storageValue || undefined;
};

export const useSharedFlowStateContext = () => {
  const storageValue = useContext(StorageCheckoutContext);
  if (!storageValue) throw new Error('CheckoutContextProvider is missing!');
  return storageValue?.flowState as UseFlowResult<
    SharedCheckoutData,
    SharedCheckoutStepProps
  >;
};

const useFlowWithSharedConfig = <Data, Props>(
  // prettier-ignore
  {
    stepTransitionConfig,
    parsedQuery: { referrer },
    ...config
  }:
    Pick<FlowConfig<Data, Props>, 'initialValues' | 'steps'>
    & {
      stepTransitionConfig: Pick<
        FlowConfig<Data, Props>['stepTransitionConfig'],
        'flowVersion' | 'transform' | 'metadata'
      >;
    } & {
      parsedQuery: ReturnType<typeof parseCheckoutQueryString>
    },
) => {
  return useFlow<Data, Props>({
    wtConfig: false,
    stepTransitionConfig: {
      resourceToken: WT_VISITOR_TOKEN,
      resourceType: 'Visitor',
      flowName: 'inline_checkout',
      ...stepTransitionConfig,
      metadata: {
        ref: referrer,
        ...stepTransitionConfig?.metadata,
      },
    },
    ...config,
  } as FlowConfig<Data, Props>);
};

const StorageProviderInner: React.FC<{
  children: React.ReactNode;
  initialValues: StorageCheckoutData;
  isChallengerFunnel: boolean;
  draftId?: string;
}> = ({ initialValues, children, isChallengerFunnel, draftId }) => {
  const client = useApolloClient();
  const flowVersion = resolveStorageCheckoutFlowVersion(isChallengerFunnel);
  const [parsedQuery] = useState(() =>
    parseCheckoutQueryString(window.location.search),
  );
  const [resolvedSteps] = useState(() =>
    parsedQuery.zipValidated
      ? SmartStorage.filter((s) => s.name !== StorageCheckoutStep.Zip)
      : SmartStorage,
  );
  const flowState = useFlowWithSharedConfig<
    StorageCheckoutData,
    StorageCheckoutStepProps
  >({
    stepTransitionConfig: {
      flowVersion,
      transform: transformStorageStepTransitionData,
      metadata: {
        checkout_type: initialValues.checkoutType,
      },
    },
    steps: resolvedSteps,
    initialValues,
    parsedQuery,
  });

  const navigationState = useNavigationState<StorageCheckoutStep>();

  const {
    values: { zip, commitment, planSize },
  } = flowState;

  const {
    dispatch,
    data: {
      lead: { token },
    },
  } = useClientDataContext();

  const disposalEligible = useEligibleForDisposalZIP(zip);

  const { pricingSet, pricingSummary, defaultPricingSummary } =
    useStoragePricingValues(zip, commitment, planSize, {
      defaultPlanSize: isChallengerFunnel
        ? CHALLENGER_DEFAULT_PLAN_SIZE
        : undefined,
    });

  useEffect(() => {
    if (!isChallengerFunnel) return;

    const flushDraft = () => flushPendingStorageDraftUpsert();
    window.addEventListener('pagehide', flushDraft);
    return () => window.removeEventListener('pagehide', flushDraft);
  }, [isChallengerFunnel]);

  useEffect(() => {
    persistStorageSessionCheckoutData(
      client,
      flowState.values,
      token,
      pricingSet,
      flowVersion,
      isChallengerFunnel ? draftId : undefined,
    );
  }, [
    client,
    draftId,
    flowState.values,
    flowVersion,
    isChallengerFunnel,
    pricingSet,
    token,
  ]);

  useEffect(() => {
    if (zip && plausibleZIP(zip)) {
      dispatch({ type: Action.SetZip, payload: { zip } });
    }
  }, [dispatch, zip]);

  return (
    <StorageCheckoutContext.Provider
      value={{
        flowVersion,
        flowState,
        navigationState,
        resolvedSteps,
        pricingSet,
        pricingSummary,
        defaultPricingSummary,
        disposalEligible,
      }}
    >
      <WTProvider
        params={{
          metadata: { flow_instance_uuid: flowState.flowInstanceUuid },
        }}
      >
        {children}
      </WTProvider>
    </StorageCheckoutContext.Provider>
  );
};

export const CheckoutContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const agentBookingLeadToken = useAgentBookingLeadToken();
  const client = useApolloClient();
  const { tests: ssrTests } = useServerDataContext();
  const { tests: clientTests } = useAbeetContext();
  const funnelAssignment = useStorageBookingFunnelAssignment();
  const {
    dispatch,
    data: { draft },
  } = useClientDataContext();
  const { service } = useParams<{ service: 'storage' }>();
  const [initialValues, setInitialValues] = useState<InitialValues>();
  const isStorageCheckout = service === 'storage';
  const { lead_token: leadToken, customer_token: customerToken } =
    useQueryParams();
  const initialValueOptions = useMemo(() => {
    if (leadToken) {
      return {
        type: CheckoutType.Shareable,
        leadToken,
        client,
      } as const;
    }
    if (customerToken) {
      return {
        type: CheckoutType.Reonboarding,
        customerToken,
        client,
      } as const;
    }
    return { type: CheckoutType.Standard, draft } as const;
  }, [client, customerToken, draft, leadToken]);
  const deferForFunnelAssignment =
    isStorageCheckout &&
    !hasStorageBookingFunnelEnrollment(ssrTests) &&
    !funnelAssignment.isReady;

  const isChallengerFunnel = useMemo(
    () =>
      funnelAssignment.isReady
        ? funnelAssignment.isChallenger
        : isChallengerStorageBookingFunnelForInit(ssrTests, clientTests),
    [
      clientTests,
      funnelAssignment.isChallenger,
      funnelAssignment.isReady,
      ssrTests,
    ],
  );

  useEffect(() => {
    if (initialValues || deferForFunnelAssignment) {
      return;
    }

    let cancelled = false;

    (async () => {
      const { storageData } = await getInitialValues(client, {
        ...initialValueOptions,
        ...(initialValueOptions.type === CheckoutType.Standard && {
          applyChallengerDefaults: isChallengerFunnel,
        }),
      });
      if (cancelled) return;

      const { leadToken: storedLeadToken, email } = storageData;
      if (storedLeadToken && email) {
        dispatch({
          type: Action.SetLead,
          payload: { token: storedLeadToken, email },
        });
      }

      setInitialValues({ storageData });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    client,
    deferForFunnelAssignment,
    dispatch,
    initialValueOptions,
    initialValues,
    isChallengerFunnel,
  ]);

  if (!initialValues)
    return (
      <LoadingState checkoutType={initialValueOptions.type} service={service} />
    );

  return (
    <>
      {agentBookingLeadToken && <AgentBanner />}
      <StorageProviderInner
        initialValues={initialValues.storageData}
        isChallengerFunnel={isChallengerFunnel}
        draftId={draft?.id}
      >
        {children}
      </StorageProviderInner>
      <PreviousBookingWarningModal />
    </>
  );
};
